import { forwardRef } from "react";

import { Box, Stack, StackProps, useTheme } from "@mui/material";

import { InvalidThemeErrorMessage } from "../InvalidThemeErrorMessage";

import { FormDecrementButton } from "./FormDecrementButton";
import { FormIncrementButton } from "./FormIncrementButton";

import { FormTypography } from "@/forms-design-system/FormTypography/FormTypography";
import { useHasValueChanged } from "@/hooks/useHasValueChanged";

type FormCounterProps = Omit<StackProps, "onChange"> & {
  value: number;
  maxValue?: number;
  maximumReason?: string;
  onIncrementClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onDecrementClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

export const FormCounter = forwardRef<HTMLDivElement, FormCounterProps>(
  ({ value, maxValue, maximumReason, onIncrementClick, onDecrementClick, ...rest }, ref) => {
    const theme = useTheme();

    const [hasValueChanged, internalValue] = useHasValueChanged(value);

    if (!theme.palette.forms || !theme.palette.formsComponents?.counter) {
      return <InvalidThemeErrorMessage />;
    }

    const hasReachedMaxValue = maxValue !== undefined && value >= maxValue;

    return (
      <Stack display="inline-flex" direction="row" alignItems="center" gap={1} {...rest} ref={ref}>
        <FormDecrementButton onClick={onDecrementClick} />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "50px",
            height: "46px",
            backgroundColor: theme.palette.formsComponents.counter.valueBoxBackgroundColor,
            borderRadius: theme.radius(1),
            border: `1px solid ${theme.palette.formsComponents.counter.valueBoxBorderColor}`,
            userSelect: "none",
          }}
        >
          <FormTypography
            key={`${internalValue}`}
            variant="h6"
            vibe="intense"
            sx={{
              animation: hasValueChanged ? "form-counter-value-fade-in linear 100ms" : undefined,
              ["@keyframes form-counter-value-fade-in"]: {
                from: {
                  opacity: 0,
                },
                to: {
                  opacity: 1,
                },
              },
            }}
            data-test="themed-counter-value"
          >
            {internalValue}
          </FormTypography>
        </Box>
        <FormIncrementButton onClick={onIncrementClick} disabled={hasReachedMaxValue} tooltipTitle={maximumReason} />
      </Stack>
    );
  }
);

FormCounter.displayName = "FormCounter";
